import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { ConnectorService, HotGlueSourceBase } from 'src/app/core/services/connector.service';
import { UserContextService } from 'src/app/core/services/user-context.service';
import { environment } from 'src/environments/environment';

interface CTABox {
  id: string;
  name: string;
  logo: string;
  linked: boolean;
  version: string;
}

@Component({
  selector: 'app-hot-glue',
  templateUrl: './hot-glue.component.html',
  styleUrl: './hot-glue.component.css'
})
export class HotGlueComponent implements OnInit {
  /**
   * If true, the component will show linked connectors/sources.
   * If false, the component will show supported sources that have not been linked yet.
   * 
   */
  @Input() linkedSources = false;

  @Output() scriptLoaded = new EventEmitter<boolean>();

  linkCallback: (source: string, flow_id: string) => void
  cancelCallback: (tap: string, flow_id: string) => void
  unlinkCallback: (tap: string, flow_id: string) => void
  closeCallback: (id: string, flow_id: string) => void

  isLoading: boolean = true;

  connectors: CTABox[] = [];
  filteredConnectors: CTABox[] = [];
  searchQuery: string = '';
  page: number = 1;
  pageSize: number = 15;
  totalResults: number = 0;
  
  constructor(
    private hotGlueService: ConnectorService,
    private userContextService: UserContextService
  ) { }

  public get Connectors(): CTABox[] {
    return this.connectors;
  }

  async ngOnInit(): Promise<void> {
    try {
      const instanceID = this.userContextService.CurrentInstance?.ID || this.userContextService.PendingInstance?.ID;
      await this.hotGlueService.SetCurrentTenant(instanceID);
      // Load script & mount HotGlue once
      await this.hotGlueService.loadScript();
      await this.hotGlueService.mountHotGlue();

      let sources: HotGlueSourceBase[] = [];
      if (this.linkedSources) {
        sources = await this.hotGlueService.getLinkedSources(environment.HOTGLUE_FLOW_ID);
      } else {
        sources = await this.hotGlueService.getSupportedSources(environment.HOTGLUE_FLOW_ID);
        console.log('Supported sources:', sources);
        // Filter out sources that are already linked
        const linkedSources = await this.hotGlueService.getLinkedSources(environment.HOTGLUE_FLOW_ID);
        console.log('Linked sources:', linkedSources);
        sources = sources.filter(source => !linkedSources.some(linked => linked.tap === source.tap));
      }

      // Map them into your local Connector interface
      this.connectors = sources.map(source => ({
        id: source.tap,
        name: source.label,
        logo: source.icon,
        linked: this.linkedSources,
        version: source.version || 'v1'
      }));

      this.totalResults = this.connectors.length;
      this.updateFilteredResults();
    } catch (err) {
      console.error('Error initializing HotGlue:', err);
    } finally {
      this.scriptLoaded.emit(true);
      this.isLoading = false;
    }
  }

  launchHG(): void {
    this.hotGlueService.open(environment.HOTGLUE_FLOW_ID, this.linkedSources);
  }

  SetOnSourceLinkedListener(callback: (source: string, flow_id: string) => void): void {
    this.linkCallback = callback;
  }

  SetonSourceLinkCanceled(callback: (tap: string, flow_id: string) => void): void {
    this.cancelCallback = callback;
  }

  SetOnSourceUnlinkedListener(callback: (tap: string, flow_id: string) => void): void {
    this.unlinkCallback = callback;
  }

  SetOnCloseListener(callback: (id: string, flow_id: string) => void): void {
    this.closeCallback = callback;
  }

  linkConnection(connectorID: string): void {
    this.hotGlueService
      .link(environment.HOTGLUE_FLOW_ID, connectorID, {
        listeners: {
          onSourceLinked: this.linkCallback,
          onSourceLinkCanceled: this.cancelCallback,
          onPopupClose: this.closeCallback
        }
      })
      .catch(err => {
        console.error('Linking connector failed or was closed:', err);
      });
  }

  search(): void {
    this.page = 1;
    this.updateFilteredResults();
  }

  updateFilteredResults(): void {
    // Normalize and trim the search query
    const query = this.searchQuery.trim().toLowerCase();
    
    // If the query is empty, return all connectors paginated
    let results = query 
      ? this.connectors.filter(conn => {
          const nameMatch = conn.name.toLowerCase().includes(query);
          return nameMatch;
        })
      : this.connectors;

    // Sort results by relevance: prioritize connectors where the query appears earlier in the name
    if (query) {
      results = results.sort((a, b) => {
        const indexA = a.name.toLowerCase().indexOf(query);
        const indexB = b.name.toLowerCase().indexOf(query);
        // Items with the query not found in the name are pushed back
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }

    // Apply pagination
    this.filteredConnectors = results.slice(
      (this.page - 1) * this.pageSize, 
      this.page * this.pageSize
    );
  }

  nextPage(): void {
    if (this.page * this.pageSize < this.totalResults) {
      this.page++;
      this.updateFilteredResults();
    }
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.updateFilteredResults();
    }
  }

  getMinPageValue(): number {
    return Math.min(this.page * this.pageSize, this.totalResults);
  }
}
